/**
 * Imperial Units Utility Functions
 * Handles parsing and formatting of ft-in-fraction measurements
 */

/**
 * Parse imperial input to decimal inches
 * Accepts formats:
 *   - "3'-6 1/2"" or "3'6 1/2"" (feet-inches-fraction)
 *   - "42.5" (decimal inches)
 *   - "54"" (inches only)
 *   - "3'" (feet only)
 * 
 * @param {string|number} input - The input value
 * @returns {number} - Decimal inches
 */
export function parseImperialToInches(input) {
  if (typeof input === 'number') {
    return input;
  }

  if (!input || typeof input !== 'string') {
    return 0;
  }

  const trimmed = input.trim();
  
  // Check if it's already a decimal number
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return parseFloat(trimmed);
  }

  let feet = 0;
  let inches = 0;
  let fraction = 0;

  // Pattern: 3'-6 1/2" or 3'6 1/2" or 3' 6 1/2"
  const feetInchFractionPattern = /(\d+)'?\s*-?\s*(\d+)?\s*"?\s*(\d+\/\d+)?/;
  const match = trimmed.match(feetInchFractionPattern);

  if (match) {
    feet = parseInt(match[1]) || 0;
    inches = parseInt(match[2]) || 0;
    
    if (match[3]) {
      const [num, denom] = match[3].split('/').map(n => parseInt(n));
      fraction = num / denom;
    }
  } else {
    // Try inches only with fraction: 6 1/2" or 42.5"
    const inchesPattern = /(\d+(\.\d+)?)\s*"?\s*(\d+\/\d+)?/;
    const inchMatch = trimmed.match(inchesPattern);
    
    if (inchMatch) {
      inches = parseFloat(inchMatch[1]) || 0;
      
      if (inchMatch[3]) {
        const [num, denom] = inchMatch[3].split('/').map(n => parseInt(n));
        fraction = num / denom;
      }
    }
  }

  return feet * 12 + inches + fraction;
}

/**
 * Round to nearest 1/16 inch
 * @param {number} inches - Decimal inches
 * @returns {number} - Rounded to nearest 1/16
 */
function roundToSixteenth(inches) {
  return Math.round(inches * 16) / 16;
}

/**
 * Convert decimal to fraction (nearest 1/16)
 * @param {number} decimal - The decimal part
 * @returns {string} - Fraction string like "1/2" or ""
 */
function decimalToFraction(decimal) {
  if (decimal === 0) return '';
  
  const sixteenths = Math.round(decimal * 16);
  
  if (sixteenths === 0) return '';
  if (sixteenths === 16) return ''; // This would be 1 whole inch
  
  // Simplify fraction
  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
  const divisor = gcd(sixteenths, 16);
  const numerator = sixteenths / divisor;
  const denominator = 16 / divisor;
  
  return `${numerator}/${denominator}`;
}

/**
 * Format decimal inches to ft-in-fraction
 * Examples: 42.5 -> "3'-6 1/2"", 50 -> "4'-2"", 6 -> "6""
 * 
 * @param {number} totalInches - Total inches as decimal
 * @param {boolean} showDecimal - Whether to show decimal inches in parentheses
 * @returns {string} - Formatted string
 */
export function formatInchesToFtIn(totalInches, showDecimal = false) {
  if (totalInches == null || isNaN(totalInches)) {
    return '0"';
  }

  const rounded = roundToSixteenth(totalInches);
  const feet = Math.floor(rounded / 12);
  const remainingInches = rounded % 12;
  const wholeInches = Math.floor(remainingInches);
  const decimalPart = remainingInches - wholeInches;
  const fraction = decimalToFraction(decimalPart);

  let result = '';
  
  if (feet > 0) {
    result += `${feet}'`;
    if (wholeInches > 0 || fraction) {
      result += `-${wholeInches}`;
      if (fraction) {
        result += ` ${fraction}`;
      }
      result += '"';
    }
  } else {
    result += `${wholeInches}`;
    if (fraction) {
      result += ` ${fraction}`;
    }
    result += '"';
  }

  if (showDecimal) {
    result += ` (${rounded.toFixed(2)} in)`;
  }

  return result;
}

/**
 * Validate imperial measurement within range
 * @param {number} inches - Decimal inches
 * @param {number} min - Minimum inches
 * @param {number} max - Maximum inches
 * @returns {boolean} - Is valid
 */
export function validateInchesRange(inches, min, max) {
  return inches >= min && inches <= max;
}

/**
 * Common validation ranges
 */
export const VALIDATION_RANGES = {
  windowSize: { min: 6, max: 240, label: '6" to 240" (20\')' },
  glassSize: { min: 4, max: 240, label: '4" to 240" (20\')' },
  gridSize: { min: 0.25, max: 6, label: '0.25" to 6"' },
  thickness: { min: 0.06, max: 3.0, label: '0.06" (1/16") to 3"' }
};

/**
 * Common glass thickness values
 */
export const COMMON_GLASS_THICKNESSES = [
  { value: 0.125, label: '1/8"' },
  { value: 0.1875, label: '3/16"' },
  { value: 0.25, label: '1/4"' },
  { value: 0.5625, label: '9/16" (laminated)' },
  { value: 0.75, label: '3/4"' },
  { value: 1.0, label: '1"' }
];
